import type { OtuScopeInfo } from '../../../Generated/OtuScopeInfo.ts'
import type { OtuScopeOrderInfo } from '../../../Generated/OtuScopeOrderInfo.ts'
import { formatDate, parseDate } from '../../../Util/DateHandling.ts'

/** The backend's database names (the XLSX's Database column) in the XLSX's order, with the labels shown for them. */
export const DATABASE_LABELS: ReadonlyArray<readonly [string, string]> = [
  ['bold', 'BOLD'],
  ['ncbi', 'NCBI nt'],
  ['rdp', 'RDP'],
  ['silva', 'SILVA']
]

export const MIXED = 'mixed'

const databaseLabel = (name: string): string => DATABASE_LABELS.find(([key]) => key === name)?.[1] ?? name

/**
 * A stable key without the parts every key of its database shares: `bold:BOLD_Public.22-May-2026` → `22-May-2026`,
 * `ncbi:nt:v5:2026-06-26` → `v5 2026-06-26`, `GenBank271_2026-04-07` → `GenBank271`, `silva:138.2:md5:…` → `138.2`.
 */
export function shortVersion(name: string, stableKey: string): string {
  switch (name) {
    case 'bold':
      return stableKey.replace(/^bold:/, '').replace(/^BOLD_Public\./, '')
    case 'ncbi':
      return stableKey.replace(/^ncbi:nt:/, '').replace(/:/g, ' ')
    case 'rdp':
      return stableKey.split('_')[0]
    case 'silva':
      return stableKey.split(':')[1] ?? stableKey
    default:
      return stableKey
  }
}

/** The one value of [values] ignoring missing ones, [MIXED] for several, null for none. */
function single(values: ReadonlyArray<string | undefined>): string | null {
  const distinct = [...new Set(values.filter((v): v is string => !!v))]
  if (distinct.length === 0) return null
  return distinct.length === 1 ? distinct[0] : MIXED
}

const orders = (info: OtuScopeInfo): OtuScopeOrderInfo[] => info.orders ?? []

/** As the XLSX names the rows: ESVs when every order is eDNA. */
export const unitNoun = (info: OtuScopeInfo): string =>
  orders(info).length > 0 && orders(info).every((o) => o.processingType === 'EDNA') ? 'ESVs' : 'OTUs'

/** The XLSX's Report Type. */
export function reportType(info: OtuScopeInfo): string | null {
  const type = single(orders(info).map((o) => o.processingType))
  if (type === 'EDNA') return 'eDNA / ESV'
  if (type === 'METABARCODING') return 'Metabarcoding'
  return type
}

const count = (n: number): string => n.toLocaleString('en-US')

/** The unassigned reads' share of all the scope's reads, in percent, to one decimal. */
export function unassignedShare(rawReads: number, assignedRawReads: number): string {
  const total = rawReads + assignedRawReads
  if (total === 0) return '0'
  const percent = (100 * rawReads) / total
  return percent > 0 && percent < 0.1 ? '< 0.1' : percent.toFixed(1)
}

/**
 * The strip's segments: each database's version ([MIXED] where orders differ), the pipeline commit, the clustering
 * method and the sequences clustered over all orders, and the OTUs without a valid blast hit with their share of reads.
 */
export function resultInfoSegments(info: OtuScopeInfo): string[] {
  const segments: string[] = []
  for (const [name, label] of DATABASE_LABELS) {
    const keys = orders(info).map((o) => (o.databases ?? []).find((d) => d.name === name)?.stableKey)
    const version = single(keys)
    if (version !== null) segments.push(`${label} ${version === MIXED ? MIXED : shortVersion(name, version)}`)
  }
  const commit = single(orders(info).map((o) => o.gitCommitId))
  if (commit !== null) segments.push(`Pipeline ${commit === MIXED ? MIXED : commit.slice(0, 7)}`)

  const method = single(orders(info).map((o) => o.flags?.clustering_method_used))
  const sequences = orders(info)
    .map((o) => Number(o.flags?.clustering_sequence_count))
    .filter((n) => Number.isFinite(n))
  if (method !== null || sequences.length > 0) {
    const parts = [method ? `Clustering ${method}` : 'Clustering']
    if (sequences.length > 0) parts.push(`${count(sequences.reduce((a, b) => a + b, 0))} sequences`)
    segments.push(parts.join(', '))
  }

  const unassigned = info.unassigned
  if (unassigned) {
    segments.push(
      `${count(unassigned.otuCount)} ${unitNoun(info)} without a valid BLAST hit ` +
        `(${unassignedShare(unassigned.rawReads, unassigned.assignedRawReads)} % of reads)`
    )
  }
  return segments
}

export const resultInfoStripText = (info: OtuScopeInfo): string => resultInfoSegments(info).join(' · ')

/** A Version Info row as the XLSX writes it, `unknown` for what is missing; [ingestedAt] formatted for display. */
export type VersionInfoRow = {
  database: string
  stableKey: string
  runId: string
  ingestedAt: string
  fingerprint: string
  gitId: string
}

export function versionInfoRows(order: OtuScopeOrderInfo): VersionInfoRow[] {
  return (order.databases ?? []).map((d) => ({
    database: databaseLabel(d.name),
    stableKey: d.stableKey ?? 'unknown',
    runId: d.runId ?? 'unknown',
    ingestedAt: d.ingestedAt ? formatDate(parseDate(d.ingestedAt)) : 'unknown',
    fingerprint: d.fingerprint ?? 'unknown',
    gitId: d.codeVersion ?? 'unknown'
  }))
}

/** The view's settings the XLSX's Result Info reports per report. */
export type ResultSettings = { grouping: string; spin?: never } | { grouping?: never; spin: { algorithm: string; asOf: string } }

export type ResultInfoRow = { label: string; value: string }

/** The Result Info's report block: report type, grouping or the SPIN settings, and the unassigned OTUs. */
export function reportInfoRows(info: OtuScopeInfo, settings: ResultSettings): ResultInfoRow[] {
  const rows: ResultInfoRow[] = []
  const type = reportType(info)
  if (type) rows.push({ label: 'Report Type', value: type })
  if (settings.spin) {
    rows.push({ label: 'SPIN Algorithm', value: settings.spin.algorithm })
    rows.push({ label: 'SPIN Point-in-time', value: settings.spin.asOf })
  } else {
    rows.push({ label: 'Grouping Mode', value: settings.grouping })
  }
  if (info.unassigned) {
    rows.push({
      label: `${unitNoun(info)} Without Valid BLAST Hit`,
      value: `${count(info.unassigned.otuCount)} ${unitNoun(info)} (${count(info.unassigned.rawReads)} reads)`
    })
  }
  return rows
}
